import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export default function ForgotPasswordPage() {
  const { t } = useLang();
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/auth/forgot-password", { email });
      setSent(true);
      toast.success("If an account exists for that email, a reset link has been sent.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#d4af37] to-[#a3852c]">
            <Zap className="w-4 h-4 text-[#0a0b0e]" strokeWidth={3} />
          </div>
          <span className="font-display font-semibold text-white text-lg">LoftPress</span>
        </Link>
        <div className="lp-card p-8">
          <h1 className="font-display text-3xl text-white/95">{t("auth.forgot.title")}</h1>
          <p className="text-white/50 mt-2 text-sm">{t("auth.forgot.subtitle")}</p>

          {sent ? (
            <div className="mt-6 lp-glass p-6 text-center">
              <Mail className="w-8 h-8 text-[#d4af37] mx-auto" />
              <div className="mt-3 text-white/80">Check your inbox.</div>
              <div className="text-white/50 text-sm mt-1">If we have that email on file, a reset link is on its way.</div>
              <div className="text-xs text-white/40 mt-3 font-mono">
                Dev tip: in this environment emails are logged to the backend console when no RESEND_API_KEY is set.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("auth.email")}</label>
                <input type="email" required className="lp-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="you@company.com" />
              </div>
              <button type="submit" disabled={busy} className="lp-btn-primary w-full justify-center" data-testid="forgot-submit">
                {busy ? t("common.loading") : t("auth.forgot.submit")} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          <div className="mt-6 text-sm text-white/50 text-center">
            <Link to="/login" className="text-white hover:text-[#d4af37]" data-testid="forgot-to-login">Back to sign in</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
